import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';
import SEO from '../components/SEO';

/**
 * ⚠️  LEGAL REVIEW PENDING — see backend/docs/legal-review-checklist.md
 *
 * This Privacy Policy was drafted in-house and has NOT been reviewed by a
 * UK GDPR-qualified solicitor. The draft below covers the basic Article 13
 * disclosures but is known to be incomplete in the following ways. Each
 * gap below is logged in the legal-review checklist with a recommended
 * direction; the lawyer's job is to validate or correct.
 *
 *   - Subprocessor list. We use OpenAI, Anthropic, Google AI, OpenRouter,
 *     Stripe, an SMTP host, Solana RPC, and Cloudflare R2. Article 28
 *     requires each to be named with the data they receive and the
 *     transfer mechanism (UK addendum to SCCs for US-based providers).
 *   - Article 14 obligation for DD subjects. We process personal data
 *     about people who are not our customers. The lawful basis is
 *     legitimate interest (Art 6.1.f) but the LIA needs documentation
 *     and the Article 14 disclosure regime needs to be addressed.
 *   - Retention schedule. Today we keep completed reports indefinitely.
 *     A defensible retention period needs to be set and documented.
 *   - DPO contact. Currently `id@id100.com`. Decide whether a DPO is
 *     required (likely yes given scale of monitoring + special category
 *     data risk) and stand up dpo-id100@totalinfo.net if so.
 *   - International transfer mechanisms. UK addendum to EU SCCs for
 *     US-based AI providers; document signed copies in RoPA.
 *   - Records of Processing Activities (Article 30). Maintained outside
 *     this codebase; a current version must exist before scaling.
 *
 * If you bump the legally-meaningful content of this policy, also bump
 * CURRENT_POLICY_VERSION in backend/middleware/policyVersion.js (TBD)
 * and User.policyAcceptedVersion will be re-checked at next login.
 */
const POLICY_VERSION = 1;

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title="Privacy Policy — id100" description="How id100 collects, uses, and protects personal data under UK GDPR." path="/privacy" />
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-5 w-5" />Back
          </Link>
          <Logo size="sm" />
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-gray-500 mb-6">Last updated: January 2026</p>
          
          <div className="prose prose-sm max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
              <p>id100.com is operated by Totalinfo Ltd ("we", "our", "us"). We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, and protect your personal data when you use our Due Diligence Platform at id100.com.</p>
              <p>We are the data controller for the purposes of the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Data We Collect</h2>
              <p>We collect the following categories of personal data:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Account Information:</strong> Name, email address, password (encrypted)</li>
                <li><strong>Payment Information:</strong> Processed securely by Stripe; we do not store card details</li>
                <li><strong>Report Data:</strong> Information you provide for due diligence investigations</li>
                <li><strong>Usage Data:</strong> IP address, browser type, pages visited</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. How We Use Your Data</h2>
              <p>We use your personal data for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>To provide and maintain our services</li>
                <li>To process payments and manage your subscription</li>
                <li>To generate due diligence reports as requested</li>
                <li>To communicate with you about your account and services</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. AI Service Providers</h2>
              <p className="bg-blue-50 p-4 rounded-md">
                <strong>Important:</strong> We use artificial intelligence infrastructure service providers (currently Amazon Bedrock, Nebius and DeepInfra) to generate due diligence reports. These providers operate under Zero Data Retention terms and do not send queries to AI model providers. Your privacy is important to us. When you submit a report request:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Your input data is sent to these AI infrastructure providers for processing</li>
                <li>Your data is <strong>NOT used to train</strong> the AI models</li>
                <li>All inputs and outputs <strong>remain your property</strong></li>
                <li>AI infrastructure providers process data under strict Zero Data Retention contractual obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Data Retention</h2>
              <p>We retain your personal data for as long as necessary to provide our services and comply with legal obligations:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Account data: Retained until account deletion requested</li>
                <li>Report data: Retained for 7 years for compliance purposes</li>
                <li>Payment records: Retained as required by UK tax law</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5a. Data Residency and Subprocessors</h2>
              <p>id100's primary infrastructure is hosted in the United Kingdom. Customer reports, account data, and payment records are stored on UK-based servers, with daily encrypted backups to Cloudflare R2 in the European Union jurisdiction.</p>
              <p>To generate reports, id100 uses third-party AI providers as subprocessors. The full subprocessor list, including each provider's data category, transfer mechanism, and signed Data Processing Agreement status, is published in our customer trust pack and available on request from <a href="mailto:dpo-id100@totalinfo.net" className="text-red-600 underline">dpo-id100@totalinfo.net</a>.</p>
              <p>Where Zero Data Retention (ZDR) options are available with a given AI provider, id100 routes sensitive content through ZDR endpoints by default. Provider-side retention is detailed in our LLM Data Policy.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Your Rights</h2>
              <p>Under UK GDPR, you have the following rights:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your data</li>
                <li><strong>Right to Data Portability:</strong> Request your data in a portable format</li>
                <li><strong>Right to Object:</strong> Object to processing of your data</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
              </ul>
              <p className="mt-3">To exercise these rights, contact us at <a href="mailto:id100-privacy@totalinfo.net" className="text-red-600">id100-privacy@totalinfo.net</a>.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Data Security</h2>
              <p>We implement appropriate technical and organisational measures to protect your data, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Encryption of data in transit (HTTPS/TLS)</li>
                <li>Secure password hashing (bcrypt)</li>
                <li>Access controls and authentication</li>
                <li>Regular security assessments</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. International Transfers</h2>
              <p>By using this service, you consent to international transfers of personal data and information. Your data may be transferred to and processed in countries outside the UK for the purpose of providing our AI-powered due diligence services. Where this occurs, we ensure appropriate safeguards are in place, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Standard Contractual Clauses approved by the ICO</li>
                <li>Adequacy decisions where applicable</li>
              </ul>
              <p className="mt-4 font-medium">AI Model Provider Data Practices:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Our AI model providers (including Anthropic, OpenAI, Google, and OpenRouter) do not retain input or output data beyond 30 days under their API terms of service</li>
                <li>Our providers do not use your data for AI model training purposes</li>
                <li>Some of our providers operate a zero data retention policy for API customers, meaning your data is not stored at all after processing</li>
                <li>All data transmitted to AI providers is sent via encrypted connections and processed solely for the purpose of generating your due diligence report</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Contact Us</h2>
              <p>For any questions about this Privacy Policy or our data practices:</p>
              <p className="mt-2">
                <strong>id100.com</strong> (operated by Totalinfo Ltd)<br />
                Email: <a href="mailto:id100-privacy@totalinfo.net" className="text-red-600">id100-privacy@totalinfo.net</a><br />
                Website: <a href="https://id100.com" className="text-red-600" target="_blank" rel="noopener noreferrer">id100.com</a><br />
                You may also lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" className="text-red-600" target="_blank" rel="noopener noreferrer">ico.org.uk</a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
